import * as z from 'zod';

export const GetDeliverySolutionsRequest = z.object({
  lineItems: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('The ID of the line item.')
          .min(1)
          .max(100)
          .optional(),
        name: z.string().describe('Item name.').max(400).optional(),
        quantity: z
          .number()
          .int()
          .describe('The number of items ordered.')
          .min(1)
          .max(999999999)
          .optional(),
        catalogReference: z
          .object({
            catalogItemId: z
              .string()
              .describe('ID of the item within the catalog it belongs to.')
              .min(1)
              .max(36)
              .optional(),
            appId: z
              .string()
              .describe(
                'ID of the app providing the catalog.\n\nYou can get your app\'s ID from its page in the [app dashboard](https://dev.wix.com/dc3/my-apps/).\n\nFor items from Wix catalogs, the following values always apply:\n+ Wix Stores: `"215238eb-22a5-4c36-9e7b-e7c08025e04e"`\n+ Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n+ Wix Restaurants: `"9a5d83fd-8570-482e-81ab-cfa88942ee60"`'
              )
              .min(1)
              .optional(),
            options: z
              .record(z.string(), z.any())
              .describe(
                'Additional item details in `key:value` pairs.\n\nUse this optional field for more specificity with item selection. The values of the `options` field differ depending on which catalog is providing the items.\n\nFor Wix Stores products, learn more about integrating with [Catalog V3](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration)\nor [Catalog V1](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v1/catalog/e-commerce-integration), depending on [the version the site uses](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-versioning/introduction).'
              )
              .optional()
              .nullable(),
          })
          .describe("Reference to the item's origin catalog.")
          .optional(),
        physicalProperties: z
          .object({
            weight: z
              .number()
              .describe(
                'Line item weight. Measurement unit matches the weight unit specified in `weightUnit` in the request.'
              )
              .optional()
              .nullable(),
            sku: z
              .string()
              .describe(
                'Stock-keeping unit. Learn more about [SKUs](https://www.wix.com/encyclopedia/definition/stock-keeping-unit-sku).'
              )
              .max(40)
              .optional()
              .nullable(),
            shippable: z
              .boolean()
              .describe('Whether this line item is shippable.')
              .optional(),
          })
          .describe('Physical properties of the item.')
          .optional(),
        price: z
          .string()
          .describe('Price of a single item after discounts.')
          .optional(),
        totalPriceBeforeDiscount: z
          .string()
          .describe('Total line item price before discounts.')
          .optional()
          .nullable(),
        priceBeforeDiscount: z
          .string()
          .describe('Price of a single item before discounts.')
          .optional()
          .nullable(),
        totalPrice: z
          .string()
          .describe(
            'Total line item price after discounts. This is equal to `price` multiplied by `quantity`.'
          )
          .optional()
          .nullable(),
        deliveryProfileId: z
          .string()
          .describe('For delivery rates by delivery profile id')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        taxIncludedInPrice: z
          .boolean()
          .describe('whether tax is included in line item price')
          .optional()
          .nullable(),
        subscriptionSettings: z
          .object({
            frequency: z
              .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
              .optional(),
          })
          .describe('Subscription settings for the item')
          .optional(),
      })
    )
    .min(1)
    .max(300),
  options: z.object({
    destination: z
      .intersection(
        z.object({
          country: z.string().describe('Country code.').optional().nullable(),
          subdivision: z
            .string()
            .describe(
              'Subdivision. Usually a state, region, prefecture, or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
            )
            .optional()
            .nullable(),
          city: z.string().describe('City name.').optional().nullable(),
          postalCode: z
            .string()
            .describe('Zip/postal code.')
            .optional()
            .nullable(),
          addressLine2: z
            .string()
            .describe(
              'Free text providing more detailed address info. Usually contains Apt, Suite, and Floor.'
            )
            .optional()
            .nullable(),
        }),
        z.xor([
          z.object({
            streetAddress: z.never().optional(),
            addressLine: z.never().optional(),
          }),
          z.object({
            addressLine: z.never().optional(),
            streetAddress: z
              .object({
                number: z.string().describe('Street number.').optional(),
                name: z.string().describe('Street name.').optional(),
              })
              .describe('Street name and number.'),
          }),
          z.object({
            streetAddress: z.never().optional(),
            addressLine: z
              .string()
              .describe(
                'Main address line, usually street and number as free text.'
              ),
          }),
        ])
      )
      .describe('Destination the items should deliver to'),
    origin: z
      .intersection(
        z.object({
          country: z.string().describe('Country code.').optional().nullable(),
          subdivision: z
            .string()
            .describe(
              'Subdivision. Usually a state, region, prefecture, or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
            )
            .optional()
            .nullable(),
          city: z.string().describe('City name.').optional().nullable(),
          postalCode: z
            .string()
            .describe('Zip/postal code.')
            .optional()
            .nullable(),
          addressLine2: z
            .string()
            .describe(
              'Free text providing more detailed address info. Usually contains Apt, Suite, and Floor.'
            )
            .optional()
            .nullable(),
        }),
        z.xor([
          z.object({
            streetAddress: z.never().optional(),
            addressLine: z.never().optional(),
          }),
          z.object({
            addressLine: z.never().optional(),
            streetAddress: z
              .object({
                number: z.string().describe('Street number.').optional(),
                name: z.string().describe('Street name.').optional(),
              })
              .describe('Street name and number.'),
          }),
          z.object({
            streetAddress: z.never().optional(),
            addressLine: z
              .string()
              .describe(
                'Main address line, usually street and number as free text.'
              ),
          }),
        ])
      )
      .describe('Origin the items should deliver from')
      .optional(),
    currency: z.string().describe("Currency - Defaults to site's currency"),
    contactDetails: z
      .object({
        firstName: z
          .string()
          .describe("Contact's first name.")
          .optional()
          .nullable(),
        lastName: z
          .string()
          .describe("Contact's last name.")
          .optional()
          .nullable(),
        phone: z
          .string()
          .describe("Contact's phone number.")
          .optional()
          .nullable(),
        company: z
          .string()
          .describe("Contact's company name.")
          .optional()
          .nullable(),
        email: z
          .string()
          .describe('Email associated with the address.')
          .email()
          .optional()
          .nullable(),
        vatId: z
          .object({
            _id: z.string().describe("Customer's tax ID.").optional(),
            type: z.enum(['UNSPECIFIED', 'CPF', 'CNPJ']).optional(),
          })
          .describe('Tax info. Currently usable only in Brazil.')
          .optional(),
      })
      .describe('Contact details')
      .optional(),
    weightUnit: z.enum(['UNSPECIFIED_WEIGHT_UNIT', 'KG', 'LB']).optional(),
    userPreferences: z
      .array(
        z.object({
          appId: z
            .string()
            .describe('The delivery carrier app id')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          code: z
            .string()
            .describe('The delivery option code')
            .max(250)
            .optional(),
        })
      )
      .max(5)
      .optional(),
    purchaseFlowId: z
      .string()
      .describe(
        'Persistent ID that correlates between the various eCommerce elements: cart, checkout, and order.'
      )
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional()
      .nullable(),
    externalReferences: z
      .array(
        z.object({
          appId: z
            .string()
            .describe(
              'ID of the app associated with the purchase flow.\nFor example, the Wix Pay Links app ID.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          resourceId: z
            .string()
            .describe(
              'Reference to an external resource ID. Used to link the purchase flow to a specific entity in an external system.\nFor example, a Wix Pay Link ID.'
            )
            .min(1)
            .max(100)
            .optional()
            .nullable(),
        })
      )
      .max(20)
      .optional(),
    extendedFields: z
      .object({
        namespaces: z
          .record(z.string(), z.record(z.string(), z.any()))
          .describe(
            'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
          )
          .optional(),
      })
      .describe(
        '[Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
      )
      .optional(),
  }),
});
export const GetDeliverySolutionsResponse = z.object({
  deliverySolutions: z
    .array(
      z.object({
        code: z
          .string()
          .describe(
            'Unique code that acts as an ID for a shipping rate. For example, `"usps_std_overnight"`.\nDEPRECATED: Use delivery_reference.code instead'
          )
          .max(250)
          .optional(),
        deliveryReference: z
          .object({
            code: z
              .string()
              .describe('The delivery solution code provided by the carrier')
              .max(250)
              .optional()
              .nullable(),
            appId: z
              .string()
              .describe('The carrier app id')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
          })
          .describe(
            "Represents a reference to a specific delivery solution. Combines both the carrier's code and its application identifier."
          )
          .optional(),
        title: z
          .string()
          .describe(
            'Delivery rate title. For example, `"USPS Standard Overnight Delivery"`, `"Standard"`, or `"First-Class Package International"`.'
          )
          .max(250)
          .optional(),
        logistics: z
          .object({
            deliveryTime: z
              .string()
              .describe(
                'When the item is expected to be delivered in free text. For example, `"3-5 business days"`.'
              )
              .max(1000)
              .optional()
              .nullable(),
            instructions: z
              .string()
              .describe(
                'Instructions for delivery. For example, for pickup: `"Ensure to come during business hours, and please don\'t park in the disabled spot"`.'
              )
              .max(1000)
              .optional()
              .nullable(),
            pickupDetails: z
              .object({
                address: z
                  .object({
                    city: z.string().optional().nullable(),
                    subdivision: z.string().optional().nullable(),
                    country: z.string().optional().nullable(),
                    postalCode: z.string().optional().nullable(),
                    addressLine1: z.string().optional().nullable(),
                    addressLine2: z.string().optional().nullable(),
                  })
                  .describe('Pickup address.')
                  .optional(),
                pickupMethod: z
                  .enum(['STORE_PICKUP', 'PICKUP_POINT'])
                  .describe(
                    'Pickup method.\n\nSupported values:\n+ `"STORE_PICKUP"`: When pickup is from the merchant\'s brick and mortar store.\n+ `"PICKUP_POINT"`: When item is shipped to a specified pickup point.'
                  )
                  .optional(),
              })
              .describe(
                'Pickup details. Should be returned only if order is for pickup.'
              )
              .optional(),
            deliveryTimeSlot: z
              .object({
                from: z
                  .date()
                  .describe('starting time of the delivery time slot')
                  .optional()
                  .nullable(),
                to: z
                  .date()
                  .describe('ending time of the delivery time slot')
                  .optional()
                  .nullable(),
                timeZone: z
                  .string()
                  .describe(
                    'Time zone in IANA format (e.g "America/New_York")\nIf not provided, the cart/checkout will format the time slot based on the site\'s business location'
                  )
                  .max(64)
                  .optional()
                  .nullable(),
              })
              .describe('Date and Time of the delivery option')
              .optional(),
          })
          .describe('Delivery logistics.')
          .optional(),
        cost: z
          .object({
            price: z
              .string()
              .describe(
                "The shipping rate's price. Must align with the [currency's decimal separator](https://en.wikipedia.org/wiki/ISO_4217#Active_codes)."
              )
              .optional(),
            currency: z
              .string()
              .describe(
                'Currency of the shipping rate price as a 3-letter [ISO-4217 currency code](https://en.wikipedia.org/wiki/ISO_4217). Must align with the `currency` passed to the function under the `metadata` field.'
              )
              .optional(),
            deliveryPrice: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                convertedAmount: z
                  .string()
                  .describe('Converted amount.')
                  .optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
                formattedConvertedAmount: z
                  .string()
                  .describe('Converted amount formatted with currency symbol.')
                  .optional(),
              })
              .describe(
                "The shipping rate's price. Must align with the [currency's decimal separator]"
              )
              .optional(),
            additionalCosts: z
              .array(
                z.object({
                  description: z
                    .string()
                    .describe(
                      'Description of the additional charge. For example, `"Handling fee of $5 applied for gift wrapping"`.'
                    )
                    .max(250)
                    .optional()
                    .nullable(),
                  deliveryPrice: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      convertedAmount: z
                        .string()
                        .describe('Converted amount.')
                        .optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                      formattedConvertedAmount: z
                        .string()
                        .describe(
                          'Converted amount formatted with currency symbol.'
                        )
                        .optional(),
                    })
                    .describe(
                      'Delivery price of additional charge. For example, `12.5`.'
                    )
                    .optional(),
                })
              )
              .max(100)
              .optional(),
          })
          .describe('Delivery cost.')
          .optional(),
        deliveryAllocations: z
          .array(
            z.object({
              deliveryCarrier: z
                .object({
                  appId: z
                    .string()
                    .describe('The carrier app id')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  code: z
                    .string()
                    .describe(
                      'Unique code that acts as an ID for a shipping rate. For example, `"usps_std_overnight"`.'
                    )
                    .max(250)
                    .optional(),
                })
                .describe(
                  "The delivery option's carrier details, could be multiple if the delivery option is a combination of multiple carriers"
                )
                .optional(),
              deliveryRegion: z
                .object({
                  _id: z
                    .string()
                    .describe('The delivery region id.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  name: z
                    .string()
                    .describe('The delivery region name.')
                    .max(100)
                    .optional()
                    .nullable(),
                })
                .describe(
                  'The delivery region that are relevant for this delivery solution.'
                )
                .optional(),
              applicableLineItems: z
                .object({
                  lineItemIds: z.array(z.string()).max(300).optional(),
                })
                .describe(
                  'Populated if the delivery solution is a partially supplied by this carrier.'
                )
                .optional(),
            })
          )
          .min(1)
          .max(300)
          .optional(),
        partial: z
          .boolean()
          .describe(
            "If the delivery solution is a partial and doesn't apply to all items."
          )
          .optional()
          .nullable(),
      })
    )
    .max(1000)
    .optional(),
  errors: z
    .array(
      z.object({
        appId: z
          .string()
          .describe('The carrier app id')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        errorDetails: z
          .intersection(
            z.object({
              tracing: z
                .record(z.string(), z.string())
                .describe(
                  "deprecated in API's - to enable migration from rendering arbitrary tracing to rest response"
                )
                .optional(),
            }),
            z.xor([
              z.object({
                applicationError: z.never().optional(),
                validationError: z.never().optional(),
                systemError: z.never().optional(),
              }),
              z.object({
                validationError: z.never().optional(),
                systemError: z.never().optional(),
                applicationError: z.object({
                  code: z.string().describe('Error code.').optional(),
                  description: z
                    .string()
                    .describe('Description of the error.')
                    .optional(),
                  data: z
                    .record(z.string(), z.any())
                    .describe('Data related to the error.')
                    .optional()
                    .nullable(),
                }),
              }),
              z.object({
                applicationError: z.never().optional(),
                systemError: z.never().optional(),
                validationError: z.object({
                  fieldViolations: z
                    .array(
                      z.object({
                        field: z.string().optional(),
                        description: z.string().optional(),
                        violatedRule: z
                          .enum([
                            'VALIDATION',
                            'OTHER',
                            'MAX',
                            'MIN',
                            'MAX_LENGTH',
                            'MIN_LENGTH',
                            'MAX_SIZE',
                            'MIN_SIZE',
                            'FORMAT',
                            'DECIMAL_LTE',
                            'DECIMAL_GTE',
                            'DECIMAL_LT',
                            'DECIMAL_GT',
                            'DECIMAL_MAX_SCALE',
                            'INVALID_ENUM_VALUE',
                            'REQUIRED_FIELD',
                            'FIELD_NOT_ALLOWED',
                            'ONE_OF_ALIGNMENT',
                            'EXACT_LENGTH',
                            'EXACT_SIZE',
                            'REQUIRED_ONE_OF_FIELD',
                          ])
                          .optional(),
                        ruleName: z
                          .string()
                          .describe('applicable when violated_rule=OTHER')
                          .optional()
                          .nullable(),
                        data: z
                          .record(z.string(), z.any())
                          .optional()
                          .nullable(),
                      })
                    )
                    .optional(),
                }),
              }),
              z.object({
                applicationError: z.never().optional(),
                validationError: z.never().optional(),
                systemError: z.object({
                  errorCode: z
                    .string()
                    .describe('Error code.')
                    .optional()
                    .nullable(),
                }),
              }),
            ])
          )
          .describe('The error details')
          .optional(),
      })
    )
    .max(50)
    .optional(),
});
